import React, { useState, useEffect } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Sun, Moon } from 'lucide-react';
import { useTheme } from './ThemeContext';

const MIN_ZOOM = 0.75;
const MAX_ZOOM = 1.5;
const STEP = 0.1;
const STORAGE_KEY = 'labanquita_zoom';

export default function ZoomControl() {
  const { theme, toggleTheme } = useTheme();
  const [zoom, setZoom] = useState(() => {
    const saved = parseFloat(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(saved) ? saved : 1;
  });

  useEffect(() => {
    // font-size scaling instead of the non-standard `zoom` property: Tailwind's
    // rem-based sizing scales with the root font-size in every browser, and
    // unlike `zoom` it doesn't interact oddly with backdrop-blur/sticky layers.
    document.documentElement.style.fontSize = `${zoom * 100}%`;
    localStorage.setItem(STORAGE_KEY, zoom);
  }, [zoom]);

  const zoomIn = () => setZoom(z => Math.min(MAX_ZOOM, +(z + STEP).toFixed(2)));
  const zoomOut = () => setZoom(z => Math.max(MIN_ZOOM, +(z - STEP).toFixed(2)));
  const reset = () => setZoom(1);

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex items-center gap-1 bg-gray-900/90 dark:bg-black/90 border border-teal-500/30 rounded-lg p-1 shadow-lg backdrop-blur-sm">
      <button
        onClick={zoomOut}
        disabled={zoom <= MIN_ZOOM}
        className="p-2 text-teal-300 hover:bg-teal-500/10 rounded disabled:opacity-30 disabled:cursor-not-allowed"
        title="Reducir zoom"
      >
        <ZoomOut size={16} />
      </button>
      <button
        onClick={reset}
        className="px-2 py-2 text-xs text-gray-400 hover:text-teal-300 hover:bg-teal-500/10 rounded min-w-[3rem]"
        title="Restablecer zoom"
      >
        {Math.round(zoom * 100)}%
      </button>
      <button
        onClick={zoomIn}
        disabled={zoom >= MAX_ZOOM}
        className="p-2 text-teal-300 hover:bg-teal-500/10 rounded disabled:opacity-30 disabled:cursor-not-allowed"
        title="Aumentar zoom"
      >
        <ZoomIn size={16} />
      </button>
      {zoom !== 1 && (
        <button
          onClick={reset}
          className="p-2 text-gray-500 hover:text-white rounded"
          title="Restablecer a 100%"
        >
          <RotateCcw size={14} />
        </button>
      )}
      <div className="w-px h-5 bg-teal-500/30 mx-0.5" />
      <button
        onClick={toggleTheme}
        className="p-2 text-teal-300 hover:bg-teal-500/10 rounded"
        title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      >
        {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
      </button>
    </div>
  );
}
